import { useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useRef } from "react";
import { api } from "@/lib/api/client";
import {
    checkDocumentTimeout,
    handleDocumentError,
    handleDocumentPendingReview,
    handleDocumentTimeout,
    processDocument,
} from "../utils/documentProcessor";
import type { LoanApplicationData, useNbiForm } from "./useNbiForm";

interface UploadState {
    status: string;
    progress: number;
    fileName: string;
    documentId?: number;
    file?: File;
    size?: number;
    error?: string;
    uploadedAt?: number;
}

interface UseDocumentPollingOptions {
    applicationId: number | undefined;
    form: ReturnType<typeof useNbiForm> | undefined;
    uploadStates: UploadState[];
    onDocumentProcessed: () => void;
    defaultCurrency?: string;
}

// Returns true if we've already handled this doc for the same processing attempt (same uploadedAt).
// Returns false if the doc is new or has been re-queued with a newer uploadedAt (e.g. after dismiss).
const isAlreadyHandled = (ref: Map<number, number>, docId: number, uploadedAt?: number): boolean => {
    if (!ref.has(docId)) return false;
    const handledAt = ref.get(docId)!;
    return handledAt >= (uploadedAt ?? 0);
};

export const useDocumentPolling = ({
    applicationId,
    form,
    uploadStates,
    onDocumentProcessed,
    defaultCurrency = "AUD",
}: UseDocumentPollingOptions) => {
    // Maps docId → uploadedAt timestamp of the processing attempt we already handled.
    // Allows the same doc to be reprocessed if it re-enters the queue with a newer uploadedAt (e.g. after dismiss).
    const processingDocsRef = useRef<Map<number, number>>(new Map());

    const { data, isLoading, error } = useQuery({
        queryKey: ["globalDocumentStatus", applicationId],
        queryFn: async () => {
            if (!applicationId || !form) return null;

            const currentStates = (form.state.values as LoanApplicationData).uploadStates || [];
            const docsToCheck = currentStates.filter(
                (s: UploadState) =>
                    s.documentId && s.status !== "error" && s.status !== "success" && s.status !== "pending_review"
            );

            if (docsToCheck.length === 0) {
                return null;
            }

            const statusResponse = await api.getDocumentsStatus(applicationId);

            // Backfill size on uploadStates that don't have it yet (e.g. split docs)
            const statesNeedingSize = docsToCheck.filter((s) => !s.file && !s.size);
            if (statesNeedingSize.length > 0) {
                const updatedStates = (form.state.values as LoanApplicationData).uploadStates?.map((s: UploadState) => {
                    if (s.size || s.file) return s;
                    const match = statusResponse.documents.find((d) => d.id === s.documentId);
                    return match?.size ? { ...s, size: match.size } : s;
                });
                if (updatedStates) form.setFieldValue("uploadStates" as any, updatedStates);
            }

            for (const doc of statusResponse.documents) {
                const uploadState = docsToCheck.find((s: UploadState) => s.documentId === doc.id);
                if (!uploadState) continue;

                if (checkDocumentTimeout(uploadState)) {
                    handleDocumentTimeout(doc, form, onDocumentProcessed);
                } else if (doc.status === "failed") {
                    handleDocumentError(doc, form, onDocumentProcessed);
                } else if (
                    doc.status === "pending_review" &&
                    !isAlreadyHandled(processingDocsRef.current, doc.id, uploadState.uploadedAt)
                ) {
                    processingDocsRef.current.set(doc.id, uploadState.uploadedAt ?? Date.now());
                    handleDocumentPendingReview(doc, uploadState, form, onDocumentProcessed);
                } else if (
                    doc.status === "processed" &&
                    !isAlreadyHandled(processingDocsRef.current, doc.id, uploadState.uploadedAt)
                ) {
                    try {
                        processingDocsRef.current.set(doc.id, uploadState.uploadedAt ?? Date.now());
                        await processDocument(
                            doc,
                            uploadState,
                            applicationId,
                            form,
                            onDocumentProcessed,
                            defaultCurrency
                        );
                    } catch (error) {
                        processingDocsRef.current.delete(doc.id);
                        posthog.captureException(error instanceof Error ? error : new Error(String(error)), {
                            document_id: doc.id,
                            file_name: doc.fileName,
                            source: "frontend",
                            context: "processDocument",
                        });
                        handleDocumentError(doc, form, onDocumentProcessed);
                    }
                }
            }

            return statusResponse;
        },
        refetchInterval: 1000,
        refetchIntervalInBackground: true,
        enabled: !!applicationId && !!form,
    });

    const hasProcessing = uploadStates.some((s) => s.status === "processing" && s.documentId);

    return {
        data,
        isLoading,
        error,
        isPolling: hasProcessing,
    };
};
